/**
 * How much of the Sun the eye sees past the bodies in its own sky: the
 * overlap of the Sun's disc with each nearer disc, weighted by the drawn
 * disc's limb law, and the share of it the ground near the eye takes.
 */

import { bandEdges } from './camera.ts';
import type { OrreryBody, OrreryHandle } from './orrery.ts';
import type { WorldVec } from '../world/frame.ts';
import type { BodyRow } from '../world/types';

/** The quadratic limb law `sol.ts` draws the disc under, I/I0 = 1 - u1 (1 - mu) - u2 (1 - mu)^2. */
export const SUN_LIMB_U1 = 0.47;
export const SUN_LIMB_U2 = 0.23;

/** The corona's light against the photosphere's: what totality leaves, and a floor that keeps every ratio of the light finite. */
export const CORONA_SHARE = 1e-6;

/** The disc's light out to radius r (in disc radii), over 2 pi I0; 1 - u1/3 - u2/6 at the limb. */
function lightWithin(r: number): number {
  const r2 = r * r;
  const mu = Math.sqrt(Math.max(0, 1 - r2));
  const c = 1 - mu * mu * mu;
  return r2 - SUN_LIMB_U1 * (r2 - (2 / 3) * c) - SUN_LIMB_U2 * (2 * r2 - (4 / 3) * c - (r2 * r2) / 2);
}
const DISC_LIGHT = lightWithin(1);

/**
 * Midpoint nodes in theta over the annulus the occulter's edge crosses, with
 * r = r1 + (r2 - r1)(1 - cos theta)/2: the covered arc goes as a square root
 * at both ends of that annulus, which the substitution takes out.
 */
const NODES = 48;
const NODE_COS = new Float64Array(NODES);
const NODE_SIN = new Float64Array(NODES);
for (let i = 0; i < NODES; i++) {
  const t = ((i + 0.5) / NODES) * Math.PI;
  NODE_COS[i] = Math.cos(t);
  NODE_SIN[i] = Math.sin(t);
}

/**
 * The share of the limb-darkened Sun's light behind one disc, from the two
 * angular radii and the separation of their centres, all in the same unit.
 * The sky is taken as flat across the two discs. Each annulus of the Sun is
 * covered along an arc in closed form; inside the occulter's nearest reach
 * the annuli are wholly in or out, and the light there is closed form too.
 */
export function blockedShare(sunR: number, occR: number, sep: number): number {
  if (!(sunR > 0) || !(occR > 0) || !(sep >= 0) || sep >= sunR + occR) return 0;
  if (sep + sunR <= occR) return 1;
  const b = occR / sunR;
  const c = sep / sunR;
  const r1 = Math.min(1, Math.abs(c - b));
  const r2 = Math.min(1, c + b);
  let blocked = c < b ? lightWithin(r1) : 0;
  if (r2 > r1) {
    const half = (r2 - r1) / 2;
    let sum = 0;
    for (let i = 0; i < NODES; i++) {
      const r = r1 + half * (1 - NODE_COS[i]!);
      const k = (r * r + c * c - b * b) / (2 * r * c);
      const arc = Math.acos(Math.min(1, Math.max(-1, k))) / Math.PI;
      const m = 1 - Math.sqrt(Math.max(0, 1 - r * r));
      sum += (1 - SUN_LIMB_U1 * m - SUN_LIMB_U2 * m * m) * 2 * r * arc * half * NODE_SIN[i]!;
    }
    blocked += sum * (Math.PI / NODES);
  }
  return Math.min(1, Math.max(0, blocked / DISC_LIGHT));
}

export interface SunCoverReading {
  /** The share of the Sun's light that reaches the eye, CORONA_SHARE to 1. */
  visible: number;
  /** The body taking the most of it, or null where none overlaps. */
  occulterId: string | null;
  sunRadiusDeg: number;
  occulterRadiusDeg: number;
  separationDeg: number;
  /** The body whose disc comes nearest the Sun's, and by how far their edges miss (negative where they overlap). */
  nearestId: string | null;
  marginDeg: number;
}

export interface SunCoverDebug {
  /** `eclipse-light-check --inject ignore-occulter`: every occulter passed over. */
  ignoreOcculters: boolean;
  /** The visible share held at this value, or null for the sky's own. */
  force: number | null;
  /** `--inject sky-ignores`: the sky and the aureole take no share while the ground still does. */
  skyIgnores: boolean;
}

export interface SunCoverHandle {
  /** This frame's reading at the eye. */
  readonly eye: Readonly<SunCoverReading>;
  /**
   * The share the ground's light takes: the share at the ground under the eye
   * up to the LOCAL band's edge, easing to 1 at the REGIONAL edge, past which the ground in view is
   * far wider than any moon's penumbra and a shadow on it is a spot, not this.
   */
  readonly ground: number;
  /** How much of `ground` is the share under the eye: 1 to the LOCAL edge, 0 past the REGIONAL one. */
  readonly groundWeight: number;
  /** `ground` as the sky's drive flux reads it: the same, unless the control says otherwise. */
  readonly skyGround: number;
  /** `eye.visible` as the aureole reads it. */
  readonly skyEye: number;
  readonly debug: SunCoverDebug;
  /** The reading from any heliocentric point, scene metres, into `out`. */
  measure(eye: WorldVec, out: SunCoverReading): SunCoverReading;
  /** The frame's own reading, from the eye the frame is drawn from. */
  update(eye: WorldVec): void;
}

export interface SunCoverDeps {
  orrery(): OrreryHandle | null;
  focus(): OrreryBody | null;
  /** The focused body's own sky (`cameraRig.inLocalSky`): the only bodies near enough to cover the Sun. */
  inLocalSky(row: BodyRow): boolean;
  readonly sunRadiusM: number;
}

const DEG = 180 / Math.PI;

export function createSunCover(deps: SunCoverDeps): SunCoverHandle {
  const candidates: OrreryBody[] = [];
  let candidatesFor: OrreryBody | null = null;
  let candidatesFrom: OrreryHandle | null = null;
  const eye: SunCoverReading = {
    visible: 1, occulterId: null, sunRadiusDeg: 0, occulterRadiusDeg: 0, separationDeg: 0,
    nearestId: null, marginDeg: Infinity,
  };
  const under: SunCoverReading = { ...eye };
  const foot = { x: 0, y: 0, z: 0 };
  let ground = 1;
  let weight = 0;
  let edgesFor: OrreryBody | null = null;
  let edges = bandEdges(1);
  const debug: SunCoverDebug = { ignoreOcculters: false, force: null, skyIgnores: false };

  function syncCandidates(orrery: OrreryHandle, focus: OrreryBody | null): void {
    if (focus === candidatesFor && orrery === candidatesFrom) return;
    candidatesFor = focus;
    candidatesFrom = orrery;
    candidates.length = 0;
    if (!focus) return;
    for (const b of orrery.bodies) if (deps.inLocalSky(b.row)) candidates.push(b);
  }

  function measure(p: WorldVec, out: SunCoverReading): SunCoverReading {
    out.visible = 1;
    out.occulterId = null;
    out.occulterRadiusDeg = 0;
    out.separationDeg = 0;
    out.nearestId = null;
    out.marginDeg = Infinity;
    // The Sun is the scene's origin.
    const sx = -p.x, sy = -p.y, sz = -p.z;
    const dSun = Math.hypot(sx, sy, sz);
    const sunR = dSun > deps.sunRadiusM ? Math.asin(deps.sunRadiusM / dSun) : Math.PI / 2;
    out.sunRadiusDeg = sunR * DEG;
    const orrery = deps.orrery();
    const f = deps.focus();
    if (orrery) syncCandidates(orrery, f);
    let blocked = 0;
    let most = 0;
    if (orrery && !debug.ignoreOcculters && debug.force === null) {
      for (const b of candidates) {
        const vx = b.world.x - p.x, vy = b.world.y - p.y, vz = b.world.z - p.z;
        const d = Math.hypot(vx, vy, vz);
        const R = b.row.radius_m;
        if (!(d > R) || d >= dSun) continue;
        const dot = vx * sx + vy * sy + vz * sz;
        if (dot <= 0) continue;
        if (f) {
          // Behind the focused body, and so is the Sun it lines up with.
          const fx = f.world.x - p.x, fy = f.world.y - p.y, fz = f.world.z - p.z;
          const t = (fx * vx + fy * vy + fz * vz) / (d * d);
          if (t > 0 && t < 1) {
            const ex = fx - t * vx, ey = fy - t * vy, ez = fz - t * vz;
            if (ex * ex + ey * ey + ez * ez < f.row.radius_m * f.row.radius_m) continue;
          }
        }
        const cx = vy * sz - vz * sy, cy = vz * sx - vx * sz, cz = vx * sy - vy * sx;
        const sep = Math.atan2(Math.hypot(cx, cy, cz), dot);
        const occR = Math.asin(R / d);
        const margin = (sep - sunR - occR) * DEG;
        if (margin < out.marginDeg) {
          out.marginDeg = margin;
          out.nearestId = b.row.id;
        }
        if (sep >= sunR + occR) continue;
        const share = blockedShare(sunR, occR, sep);
        // Two moons on the disc at once is taken as two shares that do not overlap.
        blocked += share;
        if (share > most) {
          most = share;
          out.occulterId = b.row.id;
          out.occulterRadiusDeg = occR * DEG;
          out.separationDeg = sep * DEG;
        }
      }
    }
    const v = debug.force !== null ? debug.force : 1 - blocked;
    out.visible = Math.min(1, Math.max(CORONA_SHARE, v));
    return out;
  }

  function update(p: WorldVec): void {
    measure(p, eye);
    const focus = deps.focus();
    if (!focus) {
      ground = eye.visible;
      weight = 1;
      return;
    }
    const R = focus.row.radius_m;
    const dx = p.x - focus.world.x, dy = p.y - focus.world.y, dz = p.z - focus.world.z;
    const dist = Math.hypot(dx, dy, dz);
    if (edgesFor !== focus) {
      edgesFor = focus;
      edges = bandEdges(R);
    }
    const e = edges;
    const alt = dist - R;
    const w = alt <= e.LOCAL ? 1 : alt >= e.REGIONAL ? 0 : (e.REGIONAL - alt) / (e.REGIONAL - e.LOCAL);
    weight = w;
    if (w === 0) {
      ground = 1;
      return;
    }
    // The ground's own sky: 25 km up, Phobos stands a Sun's diameter off where it does from the ground.
    const k = dist > R ? R / dist : 1;
    foot.x = focus.world.x + dx * k;
    foot.y = focus.world.y + dy * k;
    foot.z = focus.world.z + dz * k;
    measure(foot, under);
    ground = 1 - (1 - under.visible) * w;
  }

  return {
    eye,
    get ground() { return ground; },
    get groundWeight() { return weight; },
    get skyGround() { return debug.skyIgnores ? 1 : ground; },
    get skyEye() { return debug.skyIgnores ? 1 : eye.visible; },
    debug,
    measure,
    update,
  };
}
